import { InitialEditorStateType } from '@lexical/react/LexicalComposer'
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $insertNodes,
  $isLineBreakNode,
  $isParagraphNode,
  $isTextNode,
  type CreateEditorArgs,
  EditorState,
  Klass,
  LexicalEditor,
  LexicalNode,
  LexicalNodeReplacement,
} from 'lexical'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { createHeadlessEditor } from '@lexical/headless'
import { $isListNode } from '@lexical/list'
import { $isHeadingNode } from '@lexical/rich-text'
import { $isTableNode, $isTableRowNode, $isTableCellNode } from '@lexical/table'
import { LEXICAL_NODES } from './nodes'
import { HTML_TYPE } from '../../../../../../common/copy'

export const getInitialLexicalEditorState = (
  initialEditorState?: InitialEditorStateType,
  initialHtmlValue?: string,
  initialTextValue?: string,
): InitialEditorStateType => {
  if (initialEditorState) {
    return initialEditorState
  }

  if (initialHtmlValue) {
    return (editor: LexicalEditor) => {
      const parser = new DOMParser()
      const dom = parser.parseFromString(initialHtmlValue, HTML_TYPE)
      const nodes = $generateNodesFromDOM(editor, dom)

      $getRoot().select()
      $insertNodes(nodes)
    }
  }

  if (initialTextValue) {
    return () => {
      const rootNode = $getRoot()
      const lines = initialTextValue.split('\n')
      for (const line of lines) {
        const paragraph = $createParagraphNode()
        const textNode = $createTextNode(line)
        paragraph.append(textNode)
        rootNode.append(paragraph)
      }
    }
  }

  return null
}

export const parseIntoHeadlessEditor = (
  lexicalState: string,
  nodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement> = LEXICAL_NODES,
  createEditorArgs?: CreateEditorArgs,
) => {
  const editor = createHeadlessEditor({
    nodes,
    ...createEditorArgs,
  })

  editor.setEditorState(editor.parseEditorState(lexicalState))
  return editor
}

export const getTextContentFromState = (lexicalState: string, nodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement> = LEXICAL_NODES) => {
  const editor = parseIntoHeadlessEditor(lexicalState, nodes)
  return getEditorTextContent(editor.getEditorState())
}

export const getEditorStateFromHtml = (value: string, nodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement> = LEXICAL_NODES) => {
  const editor = createHeadlessEditor({ nodes })
  editor.update(
    () => {
      const parser = new DOMParser()
      const dom = parser.parseFromString(value, HTML_TYPE)
      const nodes = $generateNodesFromDOM(editor, dom)
      $getRoot().select()
      $insertNodes(nodes)
    },
    { discrete: true },
  )
  return editor.getEditorState()
}

export const getEditorStateFromText = (value: string) => {
  const editor = createHeadlessEditor({ nodes: LEXICAL_NODES })
  editor.update(
    () => {
      const rootNode = $getRoot()
      const lines = value.split('\n')
      for (const line of lines) {
        const paragraph = $createParagraphNode()
        const textNode = $createTextNode(line)
        paragraph.append(textNode)
        rootNode.append(paragraph)
      }
    },
    { discrete: true },
  )
  return editor.getEditorState()
}

export const removeTopLevelEmptyParagraphs = (htmlString: string) => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, HTML_TYPE)
    const topLevelParagraphs = Array.from(doc.body.children).filter((el) => el.tagName.toLowerCase() === 'p')
    topLevelParagraphs.forEach((p) => {
      const innerHTML = p.innerHTML.trim()
      if (innerHTML === '<br>' || innerHTML === '<br />') {
        p.remove()
      }
    })
    return doc.body.innerHTML
  } catch (e) {
    console.error(e)
  }
  return htmlString
}

export const getHtmlContentFromState = (
  lexicalState: string,
  nodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement> = LEXICAL_NODES,
  createEditorArgs?: CreateEditorArgs,
) => {
  const editor = parseIntoHeadlessEditor(lexicalState, nodes, createEditorArgs)
  return editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
}

export const addHtmlContentToState = (editor: LexicalEditor, htmlContent: string) => {
  editor.update(
    () => {
      const root = $getRoot()
      const parser = new DOMParser()
      const dom = parser.parseFromString(htmlContent, HTML_TYPE)
      const nodes = $generateNodesFromDOM(editor, dom)
      for (const node of nodes) {
        if ($isTextNode(node) || $isLineBreakNode(node)) {
          const paragraphNode = $createParagraphNode()
          paragraphNode.append(node)
          root.append(paragraphNode)
        } else {
          root.append(node)
        }
      }
    },
    { discrete: true },
  )
}

export const getEmptyEditorState = () => {
  const editor = createHeadlessEditor({
    nodes: LEXICAL_NODES,
  })

  editor.update(
    () => {
      const root = $getRoot()
      const paragraphNode = $createParagraphNode()
      root.append(paragraphNode)
    },
    { discrete: true },
  )
  return JSON.stringify(editor.getEditorState().toJSON())
}

const isNodeRich = (node: LexicalNode): boolean => {
  if ($isTextNode(node)) {
    if (node.getFormat() === 0) {
      return false
    } else if (node.getTextContent().trim() === '') {
      return false
    }
  }
  if ($isLineBreakNode(node)) {
    return false
  }
  if ($isParagraphNode(node)) {
    const childrenNodes = node.getChildren()
    return childrenNodes.some((childNode) => isNodeRich(childNode))
  }
  return true
}

export const isEditorRichText = (editorState: EditorState): boolean => {
  return editorState.read(() => {
    const root = $getRoot()
    return root.getChildren().some((node) => isNodeRich(node))
  })
}

const getNodeTextContent = (node: LexicalNode): string => {
  if ($isTextNode(node) || $isListNode(node) || $isHeadingNode(node)) {
    return node.getTextContent() + '\n'
  }
  if ($isTableNode(node)) {
    return node
      .getChildren()
      .filter($isTableRowNode)
      .reduce((tableText, rowNode) => {
        const rowText = rowNode
          .getChildren()
          .filter($isTableCellNode)
          .map((cellNode) => cellNode.getTextContent())
          .join(' ')
        return tableText + rowText + '\n'
      }, '')
  }
  if ($isLineBreakNode(node)) {
    return '\n'
  }
  if ($isParagraphNode(node)) {
    const childrenNodes = node.getChildren()
    const textContent = childrenNodes.reduce((content, childNode) => content + getNodeTextContent(childNode), '')
    return textContent || '\n'
  }
  return ''
}

export const getEditorTextContent = (editorState: EditorState) => {
  return editorState.read(() => {
    const nodes = $getRoot().getChildren()
    return nodes.reduce((textContent, node) => textContent + getNodeTextContent(node), '')
  })
}

export const getEditorHtmlContent = (
  editorState: EditorState,
  nodes: ReadonlyArray<Klass<LexicalNode> | LexicalNodeReplacement> = LEXICAL_NODES,
  createEditorArgs?: CreateEditorArgs,
) => {
  if (isEditorEmpty(editorState)) {
    return null
  }
  return getHtmlContentFromState(JSON.stringify(editorState), nodes, createEditorArgs)
}

const isEditorEmpty = (editorState: EditorState) => editorState.isEmpty() || editorState.read(() => $getRoot().isEmpty())
